import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";

import { isValue } from "@config";
import type { EnergyConsumptionSummary } from "@core/apis/shared";
import { InfoCard, Last5YearsChart, Last12MonthsChart, SolidCard } from "@components";

const Y_UNIT = "kWh";

type EnergySummarySectionProps = Readonly<{
  summary: EnergyConsumptionSummary;
  isMonthlyLoading: boolean;
  isYearlyLoading: boolean;
}>;

export function EnergySummarySection({
  summary,
  isMonthlyLoading,
  isYearlyLoading,
}: EnergySummarySectionProps) {
  const { t } = useTranslation(["propai_properties"]);

  const trendValue =
    isValue(summary.totalLast12Months) && isValue(summary.totalPrevious12Months)
      ? summary.totalLast12Months - summary.totalPrevious12Months
      : 0;

  return (
    <Grid item xs={12}>
      <SolidCard title={t("energy.energy_consumption_title")}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <InfoCard
              title={t("total_consumption_last_12_months")}
              value={summary.totalLast12Months}
              unit={Y_UNIT}
              color="tertiary"
              trend={{
                title: t("total_consumption_previous_12_months"),
                value: trendValue,
                suffix: Y_UNIT,
              }}
              trendVariant="titleMedium"
              isCardRounded
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Last12MonthsChart
              title={t("last_12_months")}
              computedSeries={summary.monthlySeries}
              isLoading={isMonthlyLoading}
              color="tertiary"
              height={250}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Last5YearsChart
              title={t("last_5_years")}
              computedSeries={summary.yearlySeries}
              isLoading={isYearlyLoading}
              color="tertiary"
              height={250}
            />
          </Grid>
        </Grid>
      </SolidCard>
    </Grid>
  );
}
